<template>
    <div class="tab-content">
        <div class="image-search-container">
            <div
                class="drop-zone"
                :class="{ highlight: isHighlighted }"
                @dragover.prevent="highlightDropzone"
                @dragleave="unhighlightDropzone"
                @drop="handleDrop"
            >
                <div v-if="imagePreview" class="image-preview">
                    <img :src="imagePreview" alt="Preview" />
                    <button class="remove-preview" @click="removePreview">×</button>
                </div>
                <template v-else>
                    <p>Перетащите изображение сюда или</p>
                    <button class="btn secondary" @click="triggerFileInput">Выберите файл</button>
                    <input
                        ref="fileInput"
                        type="file"
                        accept="image/jpeg,image/png"
                        style="display: none"
                        @change="handleFileSelect"
                    />
                    <p>Максимальный размер: 5MB</p>
                </template>
            </div>

            <div class="or-separator">
                <span>ИЛИ</span>
            </div>

            <div class="url-input-group">
                <div v-if="urlPreview" class="url-preview-container">
                    <img :src="urlPreview" alt="URL Preview" @error="handlePreviewError" />
                </div>
                <input
                    v-model="imageUrl"
                    type="text"
                    placeholder="Вставьте URL изображения"
                    @input="handleUrlInput"
                />
            </div>

            <div class="image-search-params">
                <div class="param-group">
                    <label for="threshold-slider">Порог сходства:</label>
                    <input
                        id="threshold-slider"
                        v-model.number="threshold"
                        type="range"
                        min="0"
                        max="1"
                        step="0.01"
                    />
                    <span>{{ threshold.toFixed(2) }}</span>
                </div>
                <div class="param-group">
                    <label for="top-k">Количество результатов:</label>
                    <select id="top-k" v-model="topK">
                        <option value="5">5</option>
                        <option value="10">10</option>
                        <option value="15">15</option>
                        <option value="20">20</option>
                    </select>
                </div>
            </div>

            <div class="filter-buttons">
                <button
                    class="btn"
                    :disabled="!searchEnabled || isLoading"
                    @click="performImageSearch"
                >
                    <span v-if="isLoading">Поиск...</span>
                    <span v-else>Найти</span>
                </button>
            </div>
        </div>

        <div class="stats">
            <div class="stat-item">
                <span class="stat-label">Всего планировок</span>
                <span class="stat-value">{{ imageTotalDocuments }}</span>
            </div>
            <div class="stat-item">
                <span class="stat-label">Диапазон дат</span>
                <span class="stat-value">{{ imageDateRange }}</span>
            </div>
            <div class="stat-item">
                <span class="stat-label">Найдено</span>
                <span class="stat-value">{{ imageSearchResults.items?.length || 0 }}</span>
            </div>
        </div>

        <div v-if="isLoading" class="loading">Поиск похожих планировок...</div>

        <div v-else-if="errorMessage" class="error">
            {{ errorMessage }}
        </div>

        <div v-if="isLoading" class="loading">
            <div class="spinner"></div>
            <p>Поиск похожих планировок...</p>
        </div>

        <ResultsGrid v-else :items="imageSearchResults.items" />
    </div>
</template>

<script>
    import { ref, computed, onMounted, watch } from 'vue'
    import api from '@/services/api'
    import ResultsGrid from '@/components/ResultsGrid.vue'
    import { formatDate } from '@/utils/dateFormatter.js'

    export default {
        components: { ResultsGrid },
        setup() {
            const fileInput = ref(null)
            const imagePreview = ref(null)
            const imageUrl = ref('')
            const urlPreview = ref('')
            const previewError = ref(false)
            const defaultThreshold = parseFloat(
                process.env.VUE_APP_IMAGE_SEARCH_THRESHOLD_DEFAULT || '0.5'
            )
            const threshold = ref(defaultThreshold)
            const topK = ref(10)
            const imageTotalDocuments = ref('Загрузка...')
            const imageDateRange = ref('Загрузка...')
            const imageSearchResults = ref({ items: [] })
            const isHighlighted = ref(false)
            const isLoading = ref(false)
            const errorMessage = ref('')

            watch(imageUrl, newUrl => {
                if (newUrl) {
                    previewError.value = false
                    urlPreview.value = newUrl
                } else {
                    urlPreview.value = ''
                }
            })

            function handlePreviewError() {
                previewError.value = true
                urlPreview.value = ''
            }

            function removeUrlPreview() {
                imageUrl.value = ''
                urlPreview.value = ''
                previewError.value = false
            }

            const searchEnabled = computed(() => {
                return imagePreview.value || imageUrl.value.trim()
            })

            onMounted(async () => {
                try {
                    const stats = await api.fetchImageSearchStats()
                    imageTotalDocuments.value = stats.vectorStoreTotal || 0

                    if (stats.dataMinDate && stats.dataMaxDate) {
                        const oldest = formatDate(stats.dataMinDate)
                        const newest = formatDate(stats.dataMaxDate)
                        imageDateRange.value = `${oldest} - ${newest}`
                    } else {
                        imageDateRange.value = 'Нет данных'
                    }
                } catch (error) {
                    console.error('Error fetching image search stats:', error)
                    imageTotalDocuments.value = 'Ошибка'
                    imageDateRange.value = 'Ошибка'
                }
            })

            function triggerFileInput() {
                if (fileInput.value) {
                    fileInput.value.click()
                }
            }

            function handleFileSelect(event) {
                const file = event.target.files[0]
                if (file) {
                    if (file.size > 5 * 1024 * 1024) {
                        alert('Файл слишком большой! Максимальный размер 5MB')
                        return
                    }

                    const validTypes = ['image/jpeg', 'image/png', 'image/jpg']
                    if (!validTypes.includes(file.type)) {
                        alert('Неподдерживаемый формат! Используйте JPG или PNG')
                        return
                    }

                    imagePreview.value = URL.createObjectURL(file)
                    imageUrl.value = ''
                }
            }

            function handleDrop(event) {
                event.preventDefault()
                unhighlightDropzone()

                const files = event.dataTransfer.files
                if (files && files.length > 0) {
                    const file = files[0]
                    if (file) {
                        if (file.size > 5 * 1024 * 1024) {
                            alert('Файл слишком большой! Максимальный размер 5MB')
                            return
                        }

                        const validTypes = ['image/jpeg', 'image/png', 'image/jpg']
                        if (!validTypes.includes(file.type)) {
                            alert('Неподдерживаемый формат! Используйте JPG или PNG')
                            return
                        }

                        imagePreview.value = URL.createObjectURL(file)
                        imageUrl.value = ''
                    }
                }
            }

            function highlightDropzone() {
                isHighlighted.value = true
            }

            function unhighlightDropzone() {
                isHighlighted.value = false
            }

            function removePreview() {
                imagePreview.value = null
                if (fileInput.value) {
                    fileInput.value.value = ''
                }
            }

            function handleUrlInput() {
                previewError.value = false
                if (imageUrl.value.trim()) {
                    imagePreview.value = null
                    if (fileInput.value) {
                        fileInput.value.value = ''
                    }
                }
            }

            async function performImageSearch() {
                isLoading.value = true
                errorMessage.value = ''
                imageSearchResults.value = { items: [] }

                try {
                    const formData = new FormData()

                    if (imagePreview.value) {
                        const blob = await fetch(imagePreview.value).then(r => r.blob())
                        formData.append('image', blob, 'image.jpg')
                    } else if (imageUrl.value) {
                        formData.append('imageUrl', imageUrl.value)
                    }

                    formData.append('thresholdScore', threshold.value.toString())
                    formData.append('top_k', topK.value.toString())

                    const results = await api.performImageSearch(formData)
                    imageSearchResults.value = results
                } catch (error) {
                    console.error('Image search error:', error)
                    errorMessage.value =
                        'Произошла ошибка при поиске: ' +
                        (error.response?.data?.detail || error.message)
                } finally {
                    isLoading.value = false
                }
            }

            return {
                fileInput,
                imagePreview,
                imageUrl,
                threshold,
                topK,
                imageTotalDocuments,
                imageDateRange,
                imageSearchResults,
                searchEnabled,
                isHighlighted,
                isLoading,
                errorMessage,
                triggerFileInput,
                handleFileSelect,
                handleDrop,
                highlightDropzone,
                unhighlightDropzone,
                removePreview,
                handleUrlInput,
                performImageSearch,
                urlPreview,
                previewError,
                handlePreviewError,
                removeUrlPreview
            }
        }
    }
</script>

<style scoped>
    .loading,
    .error {
        text-align: center;
        padding: 40px;
        font-size: 18px;
    }

    .error {
        color: var(--error-color);
    }

    .highlight {
        border-color: var(--primary-color);
        background-color: rgba(74, 108, 247, 0.05);
    }

    .image-search-container.disabled {
        pointer-events: none;
        opacity: 0.7;
    }

    .spinner {
        border: 4px solid rgba(0, 0, 0, 0.1);
        border-radius: 50%;
        border-top: 4px solid var(--primary-color);
        width: 40px;
        height: 40px;
        animation: spin 1s linear infinite;
        margin: 0 auto 20px;
    }

    @keyframes spin {
        0% {
            transform: rotate(0deg);
        }
        100% {
            transform: rotate(360deg);
        }
    }

    .loading {
        text-align: center;
        padding: 40px;
        font-size: 18px;
    }

    .url-preview-container {
        max-height: 200px;
        max-width: 100%;
        margin-bottom: 15px;
        display: flex;
        justify-content: center;
        align-items: center;
        border: 1px solid var(--border-color);
        border-radius: 8px;
        padding: 10px;
        background-color: #f9f9f9;
    }

    .url-preview-container img {
        max-height: 180px;
        max-width: 100%;
        object-fit: contain;
    }

    .image-preview,
    .url-preview-container {
        position: relative;
        max-height: 350px;
        max-width: 100%;
        display: flex;
        justify-content: center;
        flex-direction: column;
        align-items: center;
        margin: 0 auto 15px;
        aspect-ratio: 16/9;
    }

    .image-preview img,
    .url-preview-container img {
        max-height: 100%;
        max-width: 100%;
        object-fit: contain;
        width: auto;
        height: auto;
        display: block;
    }
</style>
